/**
 * Language-preference storage helpers (pirx-kg89.2).
 *
 * Two independent preferences:
 *  - app language   -> drives the i18next UI language
 *  - report language -> sent to the backend when generating reports
 *
 * localStorage is the offline cache / source for anonymous and first-load
 * state; the server profile is authoritative once the user is logged in.
 */
import i18n from "@/locales";
import { safeLocalStorageGet, safeLocalStorageSet } from "./safe-local-storage";
import { MARKET_OPTIONS } from "@vardic/shared/types.ts";
import type { Market } from "@vardic/shared/types.ts";
import { resolvePrimaryIndustrySector } from "@/constants/company-form-options";

export const APP_LANGUAGE_KEY = "vardic_app_language";
export const REPORT_LANGUAGE_KEY = "vardic_report_language";
export const DEFAULT_MARKET_KEY = "vardic_default_market";
export const DEFAULT_INDUSTRY_SECTOR_KEY = "vardic_default_industry_sector";

export type AppLanguage = "pl" | "en";

/** Fallback default market — mirrors the DB column default and the prior
 * hardcoded audit-form geographicScope default. */
export const FALLBACK_MARKET: Market = "Polska";

function isSupported(value: unknown): value is AppLanguage {
  return value === "pl" || value === "en";
}

function isMarket(value: unknown): value is Market {
  return typeof value === "string" &&
    (MARKET_OPTIONS as readonly string[]).includes(value);
}

export function getStoredAppLanguage(): AppLanguage {
  const stored = safeLocalStorageGet(APP_LANGUAGE_KEY);
  return isSupported(stored) ? stored : "pl";
}

export function getStoredReportLanguage(): AppLanguage {
  const stored = safeLocalStorageGet(REPORT_LANGUAGE_KEY);
  return isSupported(stored) ? stored : "pl";
}

/** Persist the UI language locally and switch i18next to it. */
export function setAppLanguage(language: AppLanguage): void {
  safeLocalStorageSet(APP_LANGUAGE_KEY, language);
  void i18n.changeLanguage(language);
}

/** Persist the report language locally. */
export function setReportLanguage(language: AppLanguage): void {
  safeLocalStorageSet(REPORT_LANGUAGE_KEY, language);
}

/** Read the stored default target market, falling back to Polska. */
export function getStoredDefaultMarket(): Market {
  const stored = safeLocalStorageGet(DEFAULT_MARKET_KEY);
  return isMarket(stored) ? stored : FALLBACK_MARKET;
}

/** Persist the default target market locally. */
export function setDefaultMarket(market: Market): void {
  safeLocalStorageSet(DEFAULT_MARKET_KEY, market);
}

/** Read the stored default industry-sector NACE code, falling back to "".
 * (pirx-30g6l — seeds the audit-context form; "" means the user picks
 * manually per run.) */
export function getStoredDefaultIndustrySector(): string {
  return safeLocalStorageGet(DEFAULT_INDUSTRY_SECTOR_KEY) ?? "";
}

/** Persist the default industry-sector NACE code locally ("" clears it). */
export function setDefaultIndustrySector(code: string): void {
  safeLocalStorageSet(DEFAULT_INDUSTRY_SECTOR_KEY, code);
}

/**
 * Apply an app (UI) language change: persist it locally (localStorage +
 * i18next) and return the preferences payload to sync to the backend profile.
 *
 * UI language and analysis/output language are independent controls
 * (pirx-m6ks) — this only ever touches the app language. The analysis-language
 * control is always visible (Upload + Settings), so there is no lockstep.
 *
 * Shared by the Settings app-language handler and the nav LanguageToggle.
 */
export function applyAppLanguageChange(
  language: AppLanguage,
): { app_language: AppLanguage } {
  setAppLanguage(language);
  return { app_language: language };
}

/**
 * Sync language preferences from the (authoritative) server profile into the
 * local cache + i18next, so a logged-in user's choice follows them across
 * browsers. No-ops for fields the server does not provide.
 */
export function applyProfileLanguagePreferences(profile: {
  appLanguage?: AppLanguage;
  reportLanguage?: AppLanguage;
  defaultMarket?: string;
  companyData?: { industry_sector?: string[] | string | null } | null;
}): void {
  if (isSupported(profile.appLanguage)) {
    setAppLanguage(profile.appLanguage);
  }
  if (isSupported(profile.reportLanguage)) {
    setReportLanguage(profile.reportLanguage);
  }
  if (isMarket(profile.defaultMarket)) {
    setDefaultMarket(profile.defaultMarket);
  }
  if (profile.companyData !== undefined) {
    setDefaultIndustrySector(
      resolvePrimaryIndustrySector(profile.companyData?.industry_sector),
    );
  }
}
